import { useEffect, useState } from 'react';

import { useGetAccounts, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { Constants } from '@constants';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { IconExclamationTriangleFilled, IconHomeFilled } from '@iwoca/icons';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { useQuery } from '@tanstack/react-query';
import { differenceInMonths } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './DeclinedView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import InternationalisedName from '../../../myAccount/multiLoanDashboard/dashboardComponents/InternationalisedName';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { ApplicationLoadingCard } from '../ApplicationLoadingCard';
import { DECLINED_APPLICATION_REAPPLY } from '../MyOffers.actions';
import { RequestedProduct } from '../MyOffers.types';
import { CreditCompassDeclineView } from './creditCompassDeclineView/CreditCompassDeclineView';

const DE_DECLINE_DELAY_MS = 30_000;

const useDeclineDelay = (statusDate: string | undefined) => {
    const [isDelaying, setIsDelaying] = useState(() => getCountry() === 'DE');

    useEffect(() => {
        if (getCountry() !== 'DE') return;
        if (!statusDate) return;

        const elapsed = Date.now() - new Date(statusDate).getTime();
        const remaining = DE_DECLINE_DELAY_MS - elapsed;

        if (remaining <= 0) {
            setIsDelaying(false);
            return;
        }

        const timer = setTimeout(() => {
            setIsDelaying(false);
        }, remaining);

        return () => clearTimeout(timer);
    }, [statusDate]);

    return isDelaying;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    reapplyFlexi: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: {
                pathname: '/apply-flexi-loan/'
            }
        }),
    declinedReapply: (requestedProduct: RequestedProduct) =>
        dispatch({
            type: DECLINED_APPLICATION_REAPPLY,
            currentRequestedProduct: requestedProduct
        })
});

const DeclinedView = ({
    reapplyFlexi,
    declinedReapply
}: ReturnType<typeof mapDispatchToProps>) => {
    const stateKey = useStateKey();

    const {
        data: application,
        isLoading: applicationLoading,
        isError: applicationError
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            })
    });

    const isDelaying = useDeclineDelay(application?.data?.status_date);
    const creditCompassSplitTestBranch = useSplitTests('credit_compass_test');
    const { data: accounts, isLoading: accountsLoading } = useGetAccounts();

    if (applicationLoading || accountsLoading) {
        return <MainContentPending />;
    }

    if (isDelaying) {
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <ApplicationLoadingCard
                    className={styles.loadingContainer}
                    data-testid="decline-delay-loading"
                />
            </MainSectionWrapper>
        );
    }

    if (applicationError || !application.data) {
        return <FallbackErrorModal />;
    }

    const hasEndedLoan = accounts?.accounts?.some((account) =>
        getAccountIsLoanEnded(account)
    );

    if (creditCompassSplitTestBranch && !hasEndedLoan) {
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <CreditCompassDeclineView />
            </MainSectionWrapper>
        );
    }

    const status = application.data.status;
    const statusDate = application.data.status_date;
    const declineStatus = application.data.decline_status;
    const requestedProduct = application.data.requested_products?.[0];

    const country = getCountry();

    const getReapplyEligibility = () => {
        if (status !== 'declined' || !declineStatus || !statusDate)
            return false;

        const monthsSinceStatusChange = differenceInMonths(
            new Date(),
            statusDate!
        );

        if (
            monthsSinceStatusChange >= 6 ||
            (country === 'UK' &&
                monthsSinceStatusChange >= 3 &&
                declineStatus === 'Soft Declined')
        )
            return true;

        return false;
    };

    const reapply = () => {
        if (requestedProduct?.product_type === 'merchant_cash_advance') {
            declinedReapply(requestedProduct);
        } else {
            reapplyFlexi();
        }
    };

    const shouldSeeReapplyCard = getReapplyEligibility() && country === 'UK';

    return shouldSeeReapplyCard ? (
        <ReapplyCard ctaOnClick={reapply} />
    ) : (
        <DeclinedCard
            ctaOnClick={reapply}
            isCTAVisible={getReapplyEligibility()}
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(DeclinedView);

const DeclinedCard = ({
    ctaOnClick,
    isCTAVisible
}: {
    ctaOnClick: () => void;
    isCTAVisible: boolean;
}) => {
    return (
        <MainSectionWrapper className={styles.wrapper}>
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <IconExclamationTriangleFilled
                        className={styles.titleIcon}
                    />
                </div>

                <div className={styles.title}>
                    <FormattedMessage
                        id="_.declined_view.title"
                        defaultMessage="Sorry, we can’t approve your application today."
                    />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-body-text"
                        id="_.declined_view.description"
                        defaultMessage="If you’d like to speak to us about the decision, or if you have any questions, give us a ring on {phoneNumber}."
                        values={{
                            phoneNumber: (
                                <a
                                    href={Constants.PHONE_NUMBER.tel}
                                    className={styles.phoneNumber}
                                >
                                    {Constants.PHONE_NUMBER.display}
                                </a>
                            )
                        }}
                    />
                </div>

                {isCTAVisible && (
                    <Button
                        size="large"
                        variant="primary"
                        className={styles.reApplyCTA}
                        data-testid="reapply-cta"
                        onClick={ctaOnClick}
                    >
                        <FormattedMessage
                            id="_.declined_view.reApplyButtonText"
                            defaultMessage="Apply"
                        />
                    </Button>
                )}
            </div>
        </MainSectionWrapper>
    );
};

const ReapplyCard = ({ ctaOnClick }: { ctaOnClick: () => void }) => {
    return (
        <MainSectionWrapper
            className={styles.wrapper}
            data-testid="reapply-card"
        >
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <IconHomeFilled className={styles.titleIcon} />
                </div>

                <div className={styles.title}>
                    <FormattedMessage
                        id="_.declined_view.reapply_title"
                        defaultMessage="Welcome back, {name}"
                        values={{ name: <InternationalisedName /> }}
                    />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-reapply-body-text"
                        id="_.declined_view.reapply_description"
                        defaultMessage="Looking to apply for funds? Click the button below to get started, or if you have any questions, give us a ring on {phoneNumber}"
                        values={{
                            phoneNumber: (
                                <a
                                    href={Constants.PHONE_NUMBER.tel}
                                    className={styles.phoneNumber}
                                >
                                    {Constants.PHONE_NUMBER.display}
                                </a>
                            )
                        }}
                    />
                </div>

                <Button
                    size="large"
                    variant="primary"
                    className={styles.reApplyCTA}
                    data-testid="reapply-cta"
                    onClick={ctaOnClick}
                >
                    <FormattedMessage
                        id="_.declined_view.reApplyButtonText"
                        defaultMessage="Apply"
                    />
                </Button>
            </div>
        </MainSectionWrapper>
    );
};
